import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowUpRight, ArrowDownRight, DollarSign, Clock, Calendar, Target, XCircle, UserX, Activity } from 'lucide-react';
import { useAuth } from '../contexts/auth/hooks';
import { supabase } from '../lib/supabase';
import { format, startOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths, eachDayOfInterval } from 'date-fns';
import toast from 'react-hot-toast';
import { RevenueChart } from '../components/statistics/RevenueChart';
import { AppointmentsChart } from '../components/statistics/AppointmentsChart';
import { ServicesChart } from '../components/statistics/ServicesChart';
import { HeatMapChart } from '../components/statistics/HeatMapChart';
import { SmartInsights, generateInsights } from '../components/statistics/SmartInsights';

export default function AdvancedStatistics() {
  const { user, business } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState<any>(null);
  const [previousMonthData, setPreviousMonthData] = useState<any>(null);
  const [revenueChartData, setRevenueChartData] = useState<any>(null);
  const [appointmentsChartData, setAppointmentsChartData] = useState<any>(null);
  const [servicesChartData, setServicesChartData] = useState<any>(null);
  const [heatMapData, setHeatMapData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('he-IL').format(num);
  };

  useEffect(() => {
    fetchAllData();
  }, [user?.id, business?.id, selectedDate]);

  const fetchAllData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      let businessId = business?.id;
      if (!businessId) {
        const { data: userData } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user.id)
          .single();
        businessId = userData?.business_id;
      }

      if (!businessId) throw new Error('לא נמצא עסק מקושר');

      const monthStart = startOfMonth(selectedDate);
      const monthEnd = endOfMonth(selectedDate);
      const prevMonthStart = startOfMonth(subMonths(selectedDate, 1));
      const prevMonthEnd = endOfMonth(subMonths(selectedDate, 1));

      const today = startOfDay(new Date());
      const weekStart = startOfWeek(today, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 0 });

      const { data: appointments, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          staff_id,
          service_id,
          status,
          users!inner (id, name),
          services (id, name_he, price),
          customers (id, name)
        `)
        .eq('business_id', businessId)
        .gte('start_time', monthStart.toISOString())
        .lt('start_time', monthEnd.toISOString());

      if (error) throw error;

      const appointmentIds = (appointments || []).map((apt: any) => apt.id);
      let invoiceMap: Record<string, string> = {};
      if (appointmentIds.length > 0) {
        const { data: payments } = await supabase
          .from('payment_transactions')
          .select('appointment_id, invoice_link, status')
          .in('appointment_id', appointmentIds)
          .eq('status', 'success');

        if (payments) {
          payments.forEach((p: any) => {
            if (p.appointment_id && p.invoice_link) {
              invoiceMap[p.appointment_id] = p.invoice_link;
            }
          });
        }
      }

      const stats = processAppointments(appointments || [], invoiceMap, today, weekStart, weekEnd);
      setStatsData(stats);

      const { data: prevAppointments } = await supabase
        .from('appointments')
        .select('id, start_time, status, services (price)')
        .eq('business_id', businessId)
        .gte('start_time', prevMonthStart.toISOString())
        .lt('start_time', prevMonthEnd.toISOString());

      const prevAppointmentIds = (prevAppointments || []).map((apt: any) => apt.id);
      let prevInvoiceMap: Record<string, string> = {};
      if (prevAppointmentIds.length > 0) {
        const { data: prevPayments } = await supabase
          .from('payment_transactions')
          .select('appointment_id, invoice_link, status')
          .in('appointment_id', prevAppointmentIds)
          .eq('status', 'success');

        if (prevPayments) {
          prevPayments.forEach((p: any) => {
            if (p.appointment_id && p.invoice_link) {
              prevInvoiceMap[p.appointment_id] = p.invoice_link;
            }
          });
        }
      }

      const prevStats = processAppointments(prevAppointments || [], prevInvoiceMap, prevMonthStart, prevMonthStart, prevMonthEnd);
      setPreviousMonthData(prevStats);

      prepareRevenueChart(appointments || [], invoiceMap, monthStart, monthEnd);
      prepareAppointmentsChart(appointments || [], invoiceMap);
      prepareServicesChart(appointments || [], invoiceMap);
      prepareHeatMap(appointments || [], invoiceMap);

    } catch (error: any) {

      toast.error('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const processAppointments = (appointments: any[], invoiceMap: Record<string, string>, today: Date, weekStart: Date, weekEnd: Date) => {
    const stats = {
      daily: { total: 0, completed: 0, revenue: 0, cancelled: 0, noShow: 0 },
      weekly: { total: 0, completed: 0, revenue: 0, cancelled: 0, noShow: 0 },
      monthly: { total: 0, completed: 0, revenue: 0, cancelled: 0, noShow: 0, totalDuration: 0 },
    };

    appointments.forEach((apt: any) => {
      const aptDate = startOfDay(new Date(apt.start_time));
      const aptEndTime = new Date(apt.end_time);
      const aptStartTime = new Date(apt.start_time);
      const duration = (aptEndTime.getTime() - aptStartTime.getTime()) / (1000 * 60);

      const price = apt.services?.price || 0;
      const isCompleted = apt.status === 'completed' && !!invoiceMap[apt.id];
      const isCancelled = apt.status === 'cancelled';
      const isNoShow = apt.status === 'no_show';

      stats.monthly.total++;
      if (isCompleted) {
        stats.monthly.completed++;
        stats.monthly.revenue += price;
        stats.monthly.totalDuration += duration;
      } else if (isCancelled) {
        stats.monthly.cancelled++;
      } else if (isNoShow) {
        stats.monthly.noShow++;
      }

      if (aptDate >= weekStart && aptDate <= weekEnd) {
        stats.weekly.total++;
        if (isCompleted) {
          stats.weekly.completed++;
          stats.weekly.revenue += price;
        } else if (isCancelled) {
          stats.weekly.cancelled++;
        } else if (isNoShow) {
          stats.weekly.noShow++;
        }
      }

      if (aptDate.getTime() === today.getTime()) {
        stats.daily.total++;
        if (isCompleted) {
          stats.daily.completed++;
          stats.daily.revenue += price;
        } else if (isCancelled) {
          stats.daily.cancelled++;
        } else if (isNoShow) {
          stats.daily.noShow++;
        }
      }
    });

    return stats;
  };

  const prepareRevenueChart = (appointments: any[], invoiceMap: Record<string, string>, monthStart: Date, monthEnd: Date) => {
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const revenueByDay = daysInMonth.map(day => {
      const dayRevenue = appointments
        .filter((apt: any) => {
          const aptDate = startOfDay(new Date(apt.start_time));
          return aptDate.getTime() === day.getTime() &&
                 apt.status === 'completed' &&
                 !!invoiceMap[apt.id];
        })
        .reduce((sum: number, apt: any) => sum + (apt.services?.price || 0), 0);
      return dayRevenue;
    });

    setRevenueChartData({
      labels: daysInMonth.map(day => format(day, 'd/M')),
      values: revenueByDay,
    });
  };

  const prepareAppointmentsChart = (appointments: any[], invoiceMap: Record<string, string>) => {
    const weekDays = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    const completed = weekDays.map(() => 0);
    const pending = weekDays.map(() => 0);
    const cancelled = weekDays.map(() => 0);

    appointments.forEach((apt: any) => {
      const dayIndex = new Date(apt.start_time).getDay();
      if (apt.status === 'completed' && !!invoiceMap[apt.id]) {
        completed[dayIndex]++;
      } else if (apt.status === 'cancelled') {
        cancelled[dayIndex]++;
      } else {
        pending[dayIndex]++;
      }
    });

    setAppointmentsChartData({
      labels: weekDays,
      completed,
      pending,
      cancelled,
    });
  };

  const prepareServicesChart = (appointments: any[], invoiceMap: Record<string, string>) => {
    const serviceMap = new Map<string, { count: number; revenue: number }>();

    appointments.forEach((apt: any) => {
      if (apt.status === 'cancelled') return;
      const serviceName = apt.services?.name_he || 'לא ידוע';
      const price = apt.services?.price || 0;
      const isCompleted = apt.status === 'completed' && !!invoiceMap[apt.id];

      if (!serviceMap.has(serviceName)) {
        serviceMap.set(serviceName, { count: 0, revenue: 0 });
      }
      const current = serviceMap.get(serviceName)!;
      current.count++;
      if (isCompleted) {
        current.revenue += price;
      }
    });

    const sortedServices = Array.from(serviceMap.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5);

    setServicesChartData({
      labels: sortedServices.map(([name]) => name),
      values: sortedServices.map(([, data]) => data.count),
      revenues: sortedServices.map(([, data]) => data.revenue),
    });
  };

  const prepareHeatMap = (appointments: any[], invoiceMap: Record<string, string>) => {
    const hours = ['9', '10', '11', '12', '13', '14', '15', '16', '17', '18'];
    const days = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    const values: number[][] = days.map(() => hours.map(() => 0));

    appointments.forEach((apt: any) => {
      if (apt.status === 'cancelled') return;
      const date = new Date(apt.start_time);
      const dayIndex = date.getDay();
      const hour = date.getHours();
      const hourIndex = hours.indexOf(hour.toString());
      if (hourIndex !== -1 && dayIndex >= 0 && dayIndex < 7) {
        values[dayIndex][hourIndex]++;
      }
    });

    setHeatMapData({ hours, days, values });
  };

  const getPercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="text-center"
        >
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
          </div>
          <p className="text-gray-700 font-semibold text-lg">טוען סטטיסטיקות...</p>
          <p className="text-gray-500 text-sm mt-2">מכין תובנות חכמות עבורך</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-24">
      {/* Header - Mobile Optimized */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="p-2.5 bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 rounded-xl shadow-lg"
              >
                <TrendingUp className="w-5 h-5 text-white" />
              </motion.div>
              <div className="flex-1">
                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  סטטיסטיקות
                </h1>
                <p className="text-xs text-gray-600 font-medium">ניתוח מעמיק ותובנות חכמות</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="month"
                value={format(selectedDate, 'yyyy-MM')}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-');
                  setSelectedDate(new Date(parseInt(year), parseInt(month) - 1));
                }}
                className="flex-1 sm:flex-initial px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-7xl mx-auto">
        {/* All Metrics - Compact Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {/* Daily Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">היום</h3>
                  <p className="text-xs text-gray-500">{statsData?.daily.completed || 0}/{statsData?.daily.total || 0}</p>
                </div>
              </div>
              {previousMonthData && (
                <div className={`flex items-center gap-1 text-xs font-semibold ${
                  getPercentageChange(statsData.daily.revenue, previousMonthData.daily.revenue) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {getPercentageChange(statsData.daily.revenue, previousMonthData.daily.revenue) >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {Math.abs(getPercentageChange(statsData.daily.revenue, previousMonthData.daily.revenue))}%
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-gray-900">₪{formatNumber(statsData?.daily.revenue || 0)}</p>
          </motion.div>

          {/* Weekly Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            transition={{ delay: 0.05, duration: 0.2 }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg">
                  <Calendar className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">השבוע</h3>
                  <p className="text-xs text-gray-500">{statsData?.weekly.completed || 0}/{statsData?.weekly.total || 0}</p>
                </div>
              </div>
              {previousMonthData && (
                <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${
                  getPercentageChange(statsData.weekly.revenue, previousMonthData.weekly.revenue) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {getPercentageChange(statsData.weekly.revenue, previousMonthData.weekly.revenue) >= 0 ? (
                    <ArrowUpRight className="w-2.5 h-2.5" />
                  ) : (
                    <ArrowDownRight className="w-2.5 h-2.5" />
                  )}
                  {Math.abs(getPercentageChange(statsData.weekly.revenue, previousMonthData.weekly.revenue))}%
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-gray-900">₪{formatNumber(statsData?.weekly.revenue || 0)}</p>
          </motion.div>

          {/* Monthly Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            transition={{ delay: 0.1, duration: 0.2 }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">החודש</h3>
                  <p className="text-xs text-gray-500">{statsData?.monthly.completed || 0}/{statsData?.monthly.total || 0}</p>
                </div>
              </div>
              {previousMonthData && (
                <div className={`flex items-center gap-1 text-xs font-semibold ${
                  getPercentageChange(statsData.monthly.revenue, previousMonthData.monthly.revenue) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {getPercentageChange(statsData.monthly.revenue, previousMonthData.monthly.revenue) >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-2.5 h-2.5" />
                  )}
                  {Math.abs(getPercentageChange(statsData.monthly.revenue, previousMonthData.monthly.revenue))}%
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-gray-900">₪{formatNumber(statsData?.monthly.revenue || 0)}</p>
          </motion.div>

          {/* Average Income */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            transition={{ delay: 0.15, duration: 0.2 }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">ממוצע</h3>
                  <p className="text-xs text-gray-500">לתור</p>
                </div>
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900">
              ₪{statsData?.monthly.completed ? formatNumber(Math.round(statsData.monthly.revenue / statsData.monthly.completed)) : 0}
            </p>
          </motion.div>

          {/* Completion Rate Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                  <Target className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">אחוז השלמה</h3>
                  <p className="text-xs text-gray-500">{statsData?.monthly.completed || 0}/{statsData?.monthly.total || 0}</p>
                </div>
              </div>
              {previousMonthData && previousMonthData.monthly.total > 0 && (
                <div className={`flex items-center gap-1 text-xs font-semibold ${
                  ((statsData.monthly.completed / statsData.monthly.total) * 100) >=
                  ((previousMonthData.monthly.completed / previousMonthData.monthly.total) * 100)
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {((statsData.monthly.completed / statsData.monthly.total) * 100) >=
                  ((previousMonthData.monthly.completed / previousMonthData.monthly.total) * 100) ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-2.5 h-2.5" />
                  )}
                  {Math.abs(Math.round(
                    ((statsData.monthly.completed / statsData.monthly.total) * 100) -
                    ((previousMonthData.monthly.completed / previousMonthData.monthly.total) * 100)
                  ))}%
                </div>
              )}
            </div>
            <p className="text-2xl font-extrabold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              {statsData?.monthly.total > 0 ? Math.round((statsData.monthly.completed / statsData.monthly.total) * 100) : 0}%
            </p>
          </motion.div>

          {/* Cancellation Rate Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg">
                  <XCircle className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">ביטולים</h3>
                  <p className="text-xs text-gray-500">{statsData?.monthly.cancelled || 0} תורים</p>
                </div>
              </div>
            </div>
            <p className={`text-2xl font-extrabold ${
              (statsData?.monthly.total > 0 && (statsData.monthly.cancelled / statsData.monthly.total) * 100 > 15)
                ? 'text-orange-600'
                : 'text-gray-900'
            }`}>
              {statsData?.monthly.total > 0 ? Math.round((statsData.monthly.cancelled / statsData.monthly.total) * 100) : 0}%
            </p>
          </motion.div>

          {/* No-Show Rate Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-red-100 to-rose-100 rounded-lg">
                  <UserX className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">לא הגיעו</h3>
                  <p className="text-xs text-gray-500">{statsData?.monthly.noShow || 0} תורים</p>
                </div>
              </div>
            </div>
            <p className={`text-2xl font-extrabold ${
              (statsData?.monthly.total > 0 && (statsData.monthly.noShow / statsData.monthly.total) * 100 > 10)
                ? 'text-red-600'
                : 'text-gray-900'
            }`}>
              {statsData?.monthly.total > 0 ? Math.round((statsData.monthly.noShow / statsData.monthly.total) * 100) : 0}%
            </p>
          </motion.div>

          {/* Average Duration Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
            className="bg-white rounded-2xl p-3 border border-gray-200 shadow-sm cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
                  <Activity className="w-4 h-4 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600">זמן ממוצע</h3>
                  <p className="text-xs text-gray-500">דק' לתור</p>
                </div>
              </div>
            </div>
            <p className="text-2xl font-extrabold text-cyan-600">
              {statsData?.monthly.completed > 0
                ? Math.round(statsData.monthly.totalDuration / statsData.monthly.completed)
                : 0}
            </p>
          </motion.div>
        </div>

        {/* Charts - Mobile Optimized */}
        <div className="space-y-4">
          {revenueChartData && <RevenueChart data={revenueChartData} />}
          {appointmentsChartData && <AppointmentsChart data={appointmentsChartData} />}
          {servicesChartData && <ServicesChart data={servicesChartData} />}
          {statsData && (
            <SmartInsights insights={generateInsights(statsData, previousMonthData)} />
          )}
          {heatMapData && <HeatMapChart data={heatMapData} />}
        </div>
      </div>
    </div>
  );
}
